import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Key,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
  Smartphone,
  ShieldAlert,
} from 'lucide-react';

interface KangavenaWhatsAppLoginProps {
  onSuccess: (token: string, tenant?: any) => void;
}

export const KangavenaWhatsAppLogin: React.FC<KangavenaWhatsAppLoginProps> = ({ onSuccess }) => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'apiKey'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const err = sessionStorage.getItem('wa_login_error');
    if (err) {
      sessionStorage.removeItem('wa_login_error');
      return err;
    }
    return null;
  });

  // Magic Pass automated device-binding verification state
  const [magicPassChecking, setMagicPassChecking] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token') || params.get('pass');
    return !!(tokenParam && tokenParam.startsWith('pass_'));
  });
  const [magicPassError, setMagicPassError] = useState<string | null>(null);
  const [magicPassToken, setMagicPassToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token') || params.get('pass');
    return tokenParam && tokenParam.startsWith('pass_') ? tokenParam : null;
  });

  const verifyMagicPass = async (passToken: string) => {
    setMagicPassChecking(true);
    setMagicPassError(null);
    try {
      const res = await fetch('/api/tenet/magic-passes/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passToken: passToken.trim() }),
      });

      const data = await res.json();

      if (data.success && data.token) {
        // Clean URL to prevent exposing token in address bar
        if (typeof window !== 'undefined' && window.history?.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }

        // Store session tokens in both local and session storage
        localStorage.setItem('tenet_client_token', data.token);
        sessionStorage.setItem('tenet_client_token', data.token);
        sessionStorage.setItem('tenet_token', data.token);
        localStorage.setItem('tenet_login_type', 'magic_pass');
        sessionStorage.setItem('tenet_login_type', 'magic_pass');
        if (data.targetPhoneNumber) {
          localStorage.setItem('tenet_magic_target_phone', data.targetPhoneNumber);
          sessionStorage.setItem('tenet_magic_target_phone', data.targetPhoneNumber);
        } else {
          localStorage.removeItem('tenet_magic_target_phone');
          sessionStorage.removeItem('tenet_magic_target_phone');
        }
        if (data.tenant) {
          localStorage.setItem('tenet_client_profile', JSON.stringify(data.tenant));
        }

        onSuccess(data.token, data.tenant);
      } else {
        setMagicPassError(
          data.error ||
            'This access pass could not be verified or has exceeded its allowed device limit.'
        );
      }
    } catch (err) {
      console.error('[MagicPassVerify] Error:', err);
      setMagicPassError('Unable to connect to authorization server. Please check your internet connection.');
    } finally {
      setMagicPassChecking(false);
    }
  };

  useEffect(() => {
    if (magicPassToken) {
      verifyMagicPass(magicPassToken);
    }
  }, [magicPassToken]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload =
        loginMethod === 'apiKey'
          ? { apiKey: apiKey.trim() }
          : { email: email.trim(), password };

      const res = await fetch('/api/tenet/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success && data.token) {
        localStorage.setItem('tenet_client_token', data.token);
        sessionStorage.setItem('tenet_client_token', data.token);
        sessionStorage.setItem('tenet_token', data.token);
        localStorage.setItem('tenet_login_type', 'credentials');
        sessionStorage.setItem('tenet_login_type', 'credentials');
        if (data.tenant) {
          localStorage.setItem('tenet_client_profile', JSON.stringify(data.tenant));
        }
        onSuccess(data.token, data.tenant);
      } else {
        setError(data.error || 'Authentication failed. Please check your credentials.');
      }
    } catch (err: any) {
      console.error('[WhatsAppLogin] Error:', err);
      setError('Unable to reach authentication server. Please check your network connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-screen h-[100dvh] bg-[#0c1317] flex flex-col justify-between items-center p-4 selection:bg-[#00a884] selection:text-white relative overflow-y-auto">
      {/* Top Brand Stripe */}
      <div className="w-full max-w-md flex items-center justify-between py-4 border-b border-[#222d34]/60">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-[#00a884] flex items-center justify-center text-[#111b21] shadow-lg shadow-[#00a884]/20">
            <MessageSquare size={20} className="fill-current" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold tracking-wide flex items-center gap-1.5">
              Kangavena <span className="text-[#00a884]">WhatsApp</span>
            </h1>
            <p className="text-[10px] text-[#8696a0] font-mono tracking-wider">LIVE CUSTOMER CONSOLE</p>
          </div>
        </div>

        <span className="text-[10px] font-mono text-[#00a884] bg-[#00a884]/10 border border-[#00a884]/30 px-2 py-0.5 rounded-full flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00a884] animate-pulse" />
          MOBILE FAST-LANE
        </span>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md my-auto py-6">
        {/* State 1: Active Verifying of Magic Pass */}
        {magicPassChecking && (
          <div className="bg-[#111b21] rounded-2xl border border-[#00a884]/40 shadow-2xl p-8 text-center relative overflow-hidden backdrop-blur-md">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-[#00a884] to-teal-400 animate-pulse" />
            <div className="w-16 h-16 rounded-2xl bg-[#00a884]/10 border border-[#00a884]/30 mx-auto flex items-center justify-center text-[#00a884] mb-4 shadow-lg">
              <RefreshCw size={28} className="animate-spin" />
            </div>
            <h2 className="text-lg font-bold text-white mb-1.5">Verifying Mobile Access Pass</h2>
            <p className="text-xs text-[#8696a0] leading-relaxed max-w-xs mx-auto">
              Binding device fingerprint and establishing encrypted WhatsApp console session...
            </p>
          </div>
        )}

        {/* State 2: Magic Pass Error / Device Limit Exceeded */}
        {!magicPassChecking && magicPassError && (
          <div className="bg-[#111b21] rounded-2xl border border-rose-500/40 shadow-2xl p-6 relative overflow-hidden backdrop-blur-md">
            <div className="absolute top-0 left-0 right-0 h-1 bg-rose-500" />
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 mx-auto flex items-center justify-center text-rose-400 mb-3 shadow-inner">
                <ShieldAlert size={28} />
              </div>
              <h2 className="text-lg font-bold text-white">Access Pass Restricted</h2>
              <p className="text-xs text-rose-400/90 mt-1.5 font-medium px-2 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20">
                {magicPassError}
              </p>
            </div>

            <div className="p-3 rounded-xl bg-[#0c1317] border border-[#222d34] text-[11px] text-[#8696a0] space-y-1.5 mb-5">
              <div className="font-bold text-[#d1d7db] flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-[#00a884]" />
                Zero-Trust Hardware Lock Active
              </div>
              <p>
                This pass enforces strict device/IP quotas. To gain access, ask your business administrator to generate a new pass or increase the device limit in the Tenet Admin Dashboard.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {magicPassToken && (
                <button
                  type="button"
                  onClick={() => verifyMagicPass(magicPassToken)}
                  className="w-full py-2.5 px-4 rounded-xl bg-[#202c33] hover:bg-[#2a3942] text-white font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  <RefreshCw size={14} />
                  <span>Retry Device Binding</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setMagicPassError(null);
                  setMagicPassToken(null);
                  if (typeof window !== 'undefined' && window.history?.replaceState) {
                    window.history.replaceState({}, document.title, window.location.pathname);
                  }
                }}
                className="w-full py-2.5 px-4 rounded-xl bg-[#00a884] hover:bg-[#00a884]/90 text-[#111b21] font-bold text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <span>Login with Email / API Key</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* State 3: Standard Credentials Login Form */}
        {!magicPassChecking && !magicPassError && (
          <div className="bg-[#111b21] rounded-2xl border border-[#222d34] shadow-2xl p-6 relative overflow-hidden backdrop-blur-md">
            {/* Subtle Top Accent Line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-[#00a884] to-teal-400" />

            {/* Heading */}
            <div className="mb-5 text-center">
              <div className="w-12 h-12 rounded-full bg-[#202c33] border border-[#2a3942] mx-auto flex items-center justify-center text-[#00a884] mb-3 shadow-inner">
                <Smartphone size={24} />
              </div>
              <h2 className="text-lg font-bold text-[#e9edef]">Sign in to WhatsApp Web</h2>
              <p className="text-xs text-[#8696a0] mt-1">
                Direct mobile access to live customer inquiries, calls & AI takeover
              </p>
            </div>

            {/* Login Method Tabs */}
            <div className="flex rounded-lg bg-[#202c33] p-1 mb-5 border border-[#2a3942]/60">
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('email');
                  setError(null);
                }}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition flex items-center justify-center gap-1.5 cursor-pointer ${
                  loginMethod === 'email'
                    ? 'bg-[#00a884] text-[#111b21] shadow-sm'
                    : 'text-[#8696a0] hover:text-white'
                }`}
              >
                <Mail size={13} />
                Email & Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('apiKey');
                  setError(null);
                }}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition flex items-center justify-center gap-1.5 cursor-pointer ${
                  loginMethod === 'apiKey'
                    ? 'bg-[#00a884] text-[#111b21] shadow-sm'
                    : 'text-[#8696a0] hover:text-white'
                }`}
              >
                <Key size={13} />
                Tenant API Key
              </button>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex items-start gap-2">
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              {loginMethod === 'email' ? (
                <>
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-[#8696a0] uppercase tracking-wider block">
                      Business Email
                    </label>
                    <div className="relative flex items-center bg-[#202c33] rounded-lg border border-[#2a3942] focus-within:border-[#00a884] transition">
                      <Mail size={16} className="text-[#8696a0] ml-3 shrink-0" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@yourbusiness.com"
                        autoComplete="username"
                        className="w-full bg-transparent px-3 py-2.5 text-sm text-[#d1d7db] placeholder-[#8696a0]/70 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-[#8696a0] uppercase tracking-wider block">
                      Password
                    </label>
                    <div className="relative flex items-center bg-[#202c33] rounded-lg border border-[#2a3942] focus-within:border-[#00a884] transition">
                      <Lock size={16} className="text-[#8696a0] ml-3 shrink-0" />
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        autoComplete="current-password"
                        className="w-full bg-transparent px-3 py-2.5 text-sm text-[#d1d7db] placeholder-[#8696a0]/70 focus:outline-none"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-[#8696a0] uppercase tracking-wider block">
                    Tenant API Key
                  </label>
                  <div className="relative flex items-center bg-[#202c33] rounded-lg border border-[#2a3942] focus-within:border-[#00a884] transition">
                    <Key size={16} className="text-[#8696a0] ml-3 shrink-0" />
                    <input
                      type="text"
                      required
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="TENET_..."
                      className="w-full bg-transparent px-3 py-2.5 text-sm text-[#d1d7db] placeholder-[#8696a0]/70 focus:outline-none font-mono"
                    />
                  </div>
                  <p className="text-[10px] text-[#8696a0] mt-1">
                    Found on your Tenet Admin Dashboard bottom left panel.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3 px-4 rounded-lg bg-[#00a884] hover:bg-[#00a884]/90 active:scale-[0.98] text-[#111b21] font-bold text-sm transition shadow-lg shadow-[#00a884]/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Connecting to Console...</span>
                  </>
                ) : (
                  <>
                    <span>Open WhatsApp Web</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            {/* Security Note */}
            <div className="mt-5 pt-4 border-t border-[#222d34] flex items-center justify-between text-[11px] text-[#8696a0]">
              <span className="flex items-center gap-1">
                <ShieldCheck size={13} className="text-[#00a884]" />
                End-to-end encrypted session
              </span>
              <a href="/login" className="text-[#00a884] hover:underline cursor-pointer">
                Full Tenet Admin
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="w-full max-w-md text-center py-3 text-[11px] text-[#8696a0] border-t border-[#222d34]/40">
        <p>Tip: Add this page to your mobile Home Screen for 1-tap app launch.</p>
      </div>
    </div>
  );
};
